import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../api/client';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  accountId: string | null;
  login: (token: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'fieldvoice_token';
const ACCOUNT_KEY = 'fieldvoice_account';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedAccount = localStorage.getItem(ACCOUNT_KEY);

    if (savedToken) {
      apiClient.setToken(savedToken);
      setToken(savedToken);
      setAccountId(savedAccount);
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const login = async (newToken: string): Promise<boolean> => {
    try {
      apiClient.setToken(newToken);

      // Validate token by making a test request
      const response = await apiClient.validateToken();

      if (response.success && response.data) {
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(ACCOUNT_KEY, response.data.account_id);
        setToken(newToken);
        setAccountId(response.data.account_id);
        setIsAuthenticated(true);
        return true;
      }

      apiClient.setToken('');
      return false;
    } catch {
      apiClient.setToken('');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    apiClient.setToken('');
    setToken(null);
    setAccountId(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, token, accountId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
